/**
 * «Έκπτωση» και «Κέρασμα» για τις ΕΠΙΛΕΓΜΕΝΕΣ γραμμές του καλαθιού τραπεζιού (απόφαση ιδιοκτήτη
 * 6/10/2026 — αντί για τις σταθερές εκπτώσεις όλης της παραγγελίας).
 *
 * Μόνο εμφάνιση και επιλογές: τα ποσά (προεπισκόπηση και εφαρμογή) βγαίνουν από το
 * utils/dineInBasketDiscount, το καλάθι τα εφαρμόζει στο DineInOperations. Ο λόγος είναι προαιρετικός.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Percent, Gift, Check, Tag } from 'lucide-react';
import NumpadModal from '../shared/NumpadModal';
import StaffReasonPicker, { NO_STAFF_REASON, StaffReasonChoice, useStaffReasonText } from './StaffReasonPicker';
import {
  AdjustableBasketLine,
  BasketDiscountKind,
  applyAmountDiscount,
  applyPercentDiscount,
  formatEuroGr,
  isPromotionLine,
  lineCents,
  lineFullCents,
  toCents,
} from '../../utils/dineInBasketDiscount';

export interface BasketActionLine extends AdjustableBasketLine {
  product_name: string;
  size_name?: string | null;
}

interface BasketActionDialogProps {
  mode: 'discount' | 'treat';
  /** Οι επιλεγμένες γραμμές, με τη σειρά του καλαθιού. */
  lines: BasketActionLine[];
  isMobile?: boolean;
  onClose: () => void;
  onApplyDiscount: (kind: BasketDiscountKind, value: number, reason: string) => void;
  onApplyTreat: (reason: string) => void;
}

const PERCENT_PRESETS = [10, 20, 50];
const AMOUNT_PRESETS = [1, 2, 5];

const BasketActionDialog: React.FC<BasketActionDialogProps> = ({
  mode, lines, isMobile, onClose, onApplyDiscount, onApplyTreat,
}) => {
  const { t } = useTranslation(['dineIn', 'common']);
  const reasonText = useStaffReasonText();
  const [kind, setKind] = useState<BasketDiscountKind>('percent');
  const [value, setValue] = useState(0);
  const [numpadOpen, setNumpadOpen] = useState(false);
  const [reason, setReason] = useState<StaffReasonChoice>(NO_STAFF_REASON);

  const isDiscount = mode === 'discount';
  const all = lines.map((_, i) => i);
  const promoLines = isDiscount ? lines.filter(isPromotionLine) : [];
  const eligible = isDiscount ? lines.filter(l => !isPromotionLine(l)) : lines;
  const grossCents = eligible.reduce((s, l) => s + lineFullCents(l), 0);
  const promoCents = promoLines.reduce((s, l) => s + lineCents(l), 0);

  // Προεπισκόπηση με τις ΙΔΙΕΣ συναρτήσεις που θα εφαρμοστούν.
  let afterCents: number | null = null;
  let capped = false;
  if (isDiscount && value > 0 && eligible.length > 0) {
    if (kind === 'percent') {
      afterCents = applyPercentDiscount(lines, all, value).reduce((s, l) => s + lineCents(l), 0) - promoCents;
    } else {
      const result = applyAmountDiscount(lines, all, value);
      afterCents = result.lines.reduce((s, l) => s + lineCents(l), 0) - promoCents;
      capped = result.capped;
    }
  }

  const presets = kind === 'percent' ? PERCENT_PRESETS : AMOUNT_PRESETS;
  const unit = kind === 'percent' ? '%' : '€';
  const formatValue = (v: number) => (kind === 'percent' ? `${v}%` : formatEuroGr(toCents(v)));
  const customValue = value > 0 && !presets.includes(value);
  const canApply = isDiscount ? value > 0 && eligible.length > 0 && grossCents > 0 : lines.length > 0;

  const title = isDiscount
    ? t('dineIn:lineActions.discountTitle', { defaultValue: 'Έκπτωση' })
    : t('dineIn:lineActions.treatTitle', { defaultValue: 'Κέρασμα' });

  const apply = () => {
    if (!canApply) return;
    if (isDiscount) onApplyDiscount(kind, value, reasonText(reason));
    else onApplyTreat(reasonText(reason));
  };

  const chip = (active: boolean) =>
    `cashier-choice rounded-xl font-bold transition-colors ${isMobile ? 'h-12 px-4 text-base' : 'h-11 px-4 text-sm'} ${
      active ? 'bg-blue-600 text-white' : 'bg-surface-2 border border-line-strong text-t1 hover:border-blue-500'
    }`;

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={`basket-${mode}-dialog`}
        className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="cashier-dialog-header px-5 py-4 bg-surface-2 border-b border-line-strong flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${isDiscount ? 'bg-purple-600' : 'bg-emerald-600'}`}>
              {isDiscount ? <Percent className="w-5 h-5 text-white" /> : <Gift className="w-5 h-5 text-white" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-t1">{title}</h2>
              <p className="text-sm text-t3">
                {t('dineIn:lineActions.linesSummary', {
                  count: lines.length,
                  amount: formatEuroGr(lines.reduce((s, l) => s + lineFullCents(l), 0)),
                  defaultValue: '{{count}} γραμμές · {{amount}}',
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
            className={`hover:bg-surface-3 rounded-xl transition-colors ${isMobile ? 'p-3' : 'p-2'}`}
          >
            <X className="w-6 h-6 text-t3" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Οι γραμμές που αφορά */}
          <ul className="space-y-1 text-sm" aria-label={t('dineIn:lineActions.selectedLines', { defaultValue: 'Επιλεγμένες γραμμές' })}>
            {lines.map((line, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <span className="text-t2 truncate">
                  {line.quantity}× {line.product_name}
                  {isDiscount && isPromotionLine(line) && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                      <Tag className="w-3 h-3" />
                      {t('dineIn:lineActions.promoTag', { defaultValue: 'προσφορά' })}
                    </span>
                  )}
                </span>
                <span className="tabular-nums text-t3">{formatEuroGr(lineFullCents(line))}</span>
              </li>
            ))}
          </ul>

          {isDiscount && (
            <>
              {/* % ή € */}
              <div className="flex gap-2" role="group" aria-label={t('dineIn:discountType', { defaultValue: 'Τύπος Έκπτωσης' })}>
                {(['percent', 'amount'] as BasketDiscountKind[]).map(k => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={kind === k}
                    onClick={() => { if (kind !== k) { setKind(k); setValue(0); } }}
                    className={`cashier-choice flex-1 rounded-xl font-bold transition-colors ${isMobile ? 'py-3.5' : 'py-3'} ${
                      kind === k ? 'bg-purple-600 text-white' : 'bg-surface-3 text-t2 hover:bg-surface-4'
                    }`}
                  >
                    {k === 'percent' ? '%' : '€'}
                  </button>
                ))}
              </div>

              {/* Γρήγορες τιμές + «Άλλο…» (πληκτρολόγιο) */}
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('dineIn:lineActions.discountValue', { defaultValue: 'Τιμή έκπτωσης' })}>
                {presets.map(p => (
                  <button key={p} type="button" aria-pressed={value === p} onClick={() => setValue(p)} className={chip(value === p)}>
                    {formatValue(p)}
                  </button>
                ))}
                <button type="button" aria-pressed={customValue} onClick={() => setNumpadOpen(true)} className={chip(customValue)}>
                  {customValue ? formatValue(value) : t('dineIn:lineActions.otherValue', { defaultValue: 'Άλλο…' })}
                </button>
              </div>

              {promoLines.length > 0 && (
                <p className="text-[13px] leading-snug text-amber-800 dark:text-amber-300" data-testid="basket-discount-promo-hint">
                  {eligible.length === 0
                    ? t('dineIn:lineActions.promoOnly', { defaultValue: 'Οι επιλεγμένες γραμμές έχουν προσφορά και κρατούν την τιμή της — δεν παίρνουν έκπτωση.' })
                    : t('dineIn:lineActions.promoSkipped', {
                        count: promoLines.length,
                        defaultValue: 'Οι γραμμές με προσφορά κρατούν την τιμή της προσφοράς — η έκπτωση πάει στις υπόλοιπες.',
                      })}
                </p>
              )}

              {afterCents !== null && (
                <dl className="p-4 bg-surface-2 rounded-xl space-y-1 text-sm" data-testid="basket-discount-preview">
                  <div className="flex justify-between text-t3">
                    <dt>{t('dineIn:lineActions.value', { defaultValue: 'Αξία' })}</dt>
                    <dd className="tabular-nums">{formatEuroGr(grossCents)}</dd>
                  </div>
                  <div className="flex justify-between font-semibold text-purple-700 dark:text-purple-300">
                    <dt>{t('dineIn:lineActions.discountTitle', { defaultValue: 'Έκπτωση' })}</dt>
                    <dd className="tabular-nums">−{formatEuroGr(grossCents - afterCents)}</dd>
                  </div>
                  <div className="flex justify-between text-base font-bold text-t1 pt-1 border-t border-line-strong">
                    <dt>{t('dineIn:newTotal', { defaultValue: 'Νέο σύνολο' })}</dt>
                    <dd className="tabular-nums">{formatEuroGr(afterCents)}</dd>
                  </div>
                  {capped && (
                    <p className="pt-1 text-[13px] text-amber-800 dark:text-amber-300">
                      {t('dineIn:lineActions.capped', {
                        amount: formatEuroGr(grossCents),
                        defaultValue: 'Η έκπτωση περιορίζεται στην αξία των γραμμών.',
                      })}
                    </p>
                  )}
                </dl>
              )}
            </>
          )}

          <StaffReasonPicker value={reason} onChange={setReason} isMobile={isMobile} />
        </div>

        <div className="px-5 py-4 border-t border-line-strong bg-surface-2 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-surface-3 text-t2 rounded-xl font-semibold hover:bg-surface-4 transition-colors"
          >
            {t('common:cancel', { defaultValue: 'Ακύρωση' })}
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!canApply}
            className={`cashier-primary flex-1 py-3 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDiscount ? 'bg-purple-600 hover:bg-purple-700' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            <Check className="w-5 h-5" />
            {t('dineIn:lineActions.apply', { defaultValue: 'Εφαρμογή' })}
          </button>
        </div>
      </div>

      <NumpadModal
        open={numpadOpen}
        initialValue={customValue ? value : 0}
        suffix={unit}
        allowDecimal={kind === 'amount'}
        title={title}
        min={0}
        max={kind === 'percent' ? 100 : Number.POSITIVE_INFINITY}
        onClose={() => setNumpadOpen(false)}
        onConfirm={(v) => { setValue(kind === 'amount' ? toCents(v) / 100 : v); setNumpadOpen(false); }}
      />
    </div>
  );
};

export default BasketActionDialog;
